import { useMaterialReview } from "@/components/MaterialBillingAttention";
// Job billing report — its own page. Per-job labour/machine/supplies cost vs
// customer invoices for a month, so un-billed work is obvious. "Invoice now"
// opens labour, plant and materials for review before invoice creation.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/pages/dashboard/_shared";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Download, Receipt, FileText, Loader2, CheckCircle2, CalendarClock, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { cutoffDate } from "@/lib/billing";
import { JobBillingDetailWorkspace } from "@/components/JobBillingDetailWorkspace";

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const monthOptions = () => {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < 24; i++) {
    out.push({ year: d.getFullYear(), month: d.getMonth() + 1, label: d.toLocaleDateString("en-NZ", { month: "long", year: "numeric" }) });
    d.setMonth(d.getMonth() - 1);
  }
  return out;
};

export default function JobBillingReport() {
  const navigate = useNavigate();
  const materialReview = useMaterialReview();
  const invoiceNow = r => materialReview.check(r.job_id, token => navigate(`/app/jobs/${r.job_id}/charge-up`, {state:{materialReviewToken:token}}));
  const opts = monthOptions();
  const [sel, setSel] = useState(opts[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [org, setOrg] = useState(null);
  const [marking, setMarking] = useState(null);
  const [cutoffInput, setCutoffInput] = useState("");
  const [detailJob, setDetailJob] = useState(null);
  useEffect(() => {
    api.get("/org/me").then((r) => {
      setOrg(r.data || null);
      setCutoffInput(r.data?.invoice_cutoff_day ?? "");
    }).catch(() => {});
  }, []);

  const saveCutoff = async () => {
    const val = cutoffInput === "" ? null : Math.min(31, Math.max(1, parseInt(cutoffInput, 10) || 0)) || null;
    if ((org?.invoice_cutoff_day ?? null) === val) return;
    try {
      const { data } = await api.patch("/org/me", { invoice_cutoff_day: val });
      setOrg(data);
      setCutoffInput(data?.invoice_cutoff_day ?? "");
      toast.success(val ? `Invoice cut-off set to the ${val}${val === 1 ? "st" : val === 2 ? "nd" : val === 3 ? "rd" : val === 21 ? "st" : val === 22 ? "nd" : val === 23 ? "rd" : val === 31 ? "st" : "th"} of the month` : "Invoice cut-off cleared — imports pull all hours");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the cut-off day");
      setCutoffInput(org?.invoice_cutoff_day ?? "");
    }
  };

  const markInvoiced = r => materialReview.check(r.job_id, async material_review_token => {
    const cut = cutoffDate(org?.invoice_cutoff_day);
    const scope = cut ? `up to ${new Date(`${cut}T00:00:00`).toLocaleDateString("en-NZ", { day: "numeric", month: "long" })}` : "to date";
    if (!window.confirm(`Mark all unclaimed hours on ${r.job_number || r.title} (${scope}) as invoiced outside Fieldlink?\n\nThey'll be locked so they can't be billed again.`)) return;
    setMarking(r.job_id);
    try {
      const { data } = await api.post(`/jobs/${r.job_id}/hours/mark-invoiced`, {to_date: cut || undefined, material_review_token});
      toast.success(`Marked as invoiced — ${data.labour_marked} labour + ${data.machine_marked} machine entr${(data.labour_marked + data.machine_marked) === 1 ? "y" : "ies"} locked`);
      load();
    } catch (e) {
      if (e.response?.data?.detail?.code === "material_review_required") throw e;
      toast.error(e.response?.data?.detail || "Could not mark as invoiced");
    } finally {
      setMarking(null);
    }
  });

  const load = async (o = sel) => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/reports/job-billing", { params: { year: o.year, month: o.month } });
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the report");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [sel.year, sel.month]);

  const step = (dir) => {
    const idx = opts.findIndex((o) => o.year === sel.year && o.month === sel.month);
    const next = opts[idx - dir];
    if (next) setSel(next);
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/reports/monthly/pdf", {
        params: { year: sel.year, month: sel.month, section: "job_billing" },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `job-billing-${sel.year}-${String(sel.month).padStart(2, "0")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF download failed");
    } finally {
      setDownloading(false);
    }
  };

  const t = data?.totals || {};
  const rows = data?.rows || [];

  return (
    <div data-testid="job-billing-page">
      <PageHeader
        title="Job billing"
        subtitle="Labour, machines & supplies vs what's been invoiced — spot un-billed work before it's forgotten"
        actions={
          <Button variant="outline" onClick={downloadPdf} disabled={downloading || loading}
                  className="rounded-sm uppercase tracking-wider font-bold text-xs border-zinc-400" data-testid="job-billing-pdf-btn">
            {downloading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} PDF
          </Button>
        }
      />

      {/* Month picker */}
      <div className="flex items-center gap-2 mb-5">
        <Button size="icon" variant="outline" className="h-9 w-9 rounded-lg border-zinc-300" onClick={() => step(-1)}
                disabled={opts.findIndex((o) => o.year === sel.year && o.month === sel.month) >= opts.length - 1}
                data-testid="job-billing-prev-month">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Select value={`${sel.year}-${sel.month}`} onValueChange={(v) => { const [y, m] = v.split("-").map(Number); setSel(opts.find((o) => o.year === y && o.month === m) || opts[0]); }}>
          <SelectTrigger className="w-[210px] h-9 rounded-lg border-zinc-300 font-bold" data-testid="job-billing-month-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {opts.map((o) => (
              <SelectItem key={`${o.year}-${o.month}`} value={`${o.year}-${o.month}`}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="icon" variant="outline" className="h-9 w-9 rounded-lg border-zinc-300" onClick={() => step(1)}
                disabled={sel.year === opts[0].year && sel.month === opts[0].month}
                data-testid="job-billing-next-month">
          <ChevronRight className="w-4 h-4" />
        </Button>

        <div className="ml-auto flex items-center gap-2 rounded-lg border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#101620] px-3 h-9" data-testid="job-billing-cutoff">
          <CalendarClock className="w-4 h-4 text-[#FF5F15]" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Invoice cut-off day</span>
          <input
            type="number" min="1" max="31"
            value={cutoffInput}
            onChange={(e) => setCutoffInput(e.target.value)}
            onBlur={saveCutoff}
            onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
            placeholder="—"
            className="w-12 h-6 text-center font-mono font-bold text-sm bg-transparent border-b border-zinc-300 dark:border-white/20 outline-none focus:border-[#FF5F15]"
            data-testid="job-billing-cutoff-input"
          />
          <span className="text-[11px] text-zinc-500 hidden sm:inline">of the month — imports pull hours up to this date</span>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          { label: "Cost this month", value: fmtMoney(t.cost), testid: "jb-total-cost" },
          { label: "Invoiced this month", value: fmtMoney(t.invoiced), testid: "jb-total-invoiced" },
          { label: "Not yet invoiced", value: fmtMoney(t.uninvoiced), warn: (t.uninvoiced || 0) > 0, testid: "jb-total-uninvoiced" },
        ].map((c) => (
          <div key={c.label} className={`rounded-xl border p-4 ${c.warn ? "border-amber-400/60 bg-amber-50 dark:bg-amber-900/10" : "border-zinc-200 dark:border-white/10 bg-white dark:bg-[#101620]"}`} data-testid={c.testid}>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{c.label}</div>
            <div className={`text-2xl font-extrabold tabular-nums mt-1 ${c.warn ? "text-amber-600 dark:text-amber-400" : ""}`}>{loading ? "…" : c.value}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-2">{[1, 2, 3, 4].map((i) => <div key={i} className="h-14 rounded-xl bg-zinc-100 dark:bg-white/5 animate-pulse" />)}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-zinc-300 dark:border-white/10 p-12 text-center text-sm text-zinc-500" data-testid="job-billing-empty">
          No job activity recorded in {data?.label || "this month"}.
        </div>
      ) : (
        <div className="rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#101620] overflow-x-auto" data-testid="job-billing-table">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-zinc-500 border-b border-zinc-200 dark:border-white/10">
                <th className="py-2.5 px-4">Job</th>
                <th className="py-2.5 px-3 text-right">Labour</th>
                <th className="py-2.5 px-3 text-right">Machines</th>
                <th className="py-2.5 px-3 text-right">Supplies</th>
                <th className="py-2.5 px-3 text-right">Cost</th>
                <th className="py-2.5 px-3 text-right">Invoiced</th>
                <th className="py-2.5 px-3 text-right">Not invoiced</th>
                <th className="py-2.5 px-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const gap = (r.uninvoiced || 0) > 0.005;
                return (
                  <tr key={r.job_id} className={`border-b border-zinc-100 dark:border-white/5 last:border-0 ${gap ? "bg-amber-50/60 dark:bg-amber-900/10" : ""}`} data-testid={`jb-row-${r.job_id}`}>
                    <td className="py-2.5 px-4">
                      <div className="flex items-center gap-2">
                        {r.job_number && <span className="font-mono text-[11px] font-bold bg-[#09090B] text-[#FF5F15] px-1.5 py-0.5 rounded">{r.job_number}</span>}
                        <div className="min-w-0">
                          <div className="font-bold truncate max-w-[260px]">{r.title}</div>
                          {r.client_name && <div className="text-[11px] text-zinc-500 truncate">{r.client_name}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="py-2.5 px-3 font-mono text-right tabular-nums">
                      <div>{fmtMoney(r.labour)}</div>
                      {(r.labour_hours ?? r.hours ?? 0) > 0 && <div className="text-[10px] text-zinc-500">{r.labour_hours ?? r.hours} hrs</div>}
                    </td>
                    <td className="py-2.5 px-3 font-mono text-right tabular-nums">
                      <div>{fmtMoney(r.machines)}</div>
                      {(r.machine_hours || 0) > 0 && <div className="text-[10px] text-zinc-500">{r.machine_hours} hrs</div>}
                    </td>
                    <td className="py-2.5 px-3 font-mono text-right tabular-nums">{fmtMoney(r.supplies)}</td>
                    <td className="py-2.5 px-3 font-mono text-right tabular-nums font-bold">{fmtMoney(r.cost)}</td>
                    <td className="py-2.5 px-3 font-mono text-right tabular-nums">{fmtMoney(r.invoiced)}</td>
                    <td className={`py-2.5 px-3 font-mono text-right tabular-nums font-bold ${gap ? "text-amber-600 dark:text-amber-400" : "text-emerald-600"}`}>
                      {fmtMoney(r.uninvoiced)}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => setDetailJob(r)}
                                title="Day-by-day breakdown — who worked, machine hours and daily summaries"
                                className="h-8 rounded-lg uppercase tracking-wider font-bold text-[11px] border-zinc-300 dark:border-white/15"
                                data-testid={`job-details-${r.job_id}`}>
                          <Eye className="w-3.5 h-3.5 mr-1.5" /> Details
                        </Button>
                        {gap && (
                          <>
                          <Button size="sm" onClick={() => invoiceNow(r)}
                                  className="h-8 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-[11px]"
                                  data-testid={`invoice-now-${r.job_id}`}>
                            <Receipt className="w-3.5 h-3.5 mr-1.5" /> Invoice now
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => markInvoiced(r)} disabled={marking === r.job_id}
                                  title="Invoiced outside Fieldlink? Lock these hours so they can't be billed again."
                                  className="h-8 rounded-lg uppercase tracking-wider font-bold text-[11px] border-zinc-300 dark:border-white/15"
                                  data-testid={`mark-invoiced-${r.job_id}`}>
                            {marking === r.job_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
                            {marking === r.job_id ? "" : "Mark invoiced"}
                          </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {materialReview.dialog}
      <JobBillingDetailWorkspace
        job={detailJob}
        sel={sel}
        onClose={() => { setDetailJob(null); load(); }}
        onInvoiceNow={invoiceNow}
        onMarkInvoiced={markInvoiced}
        marking={marking}
      />

      <div className="flex items-start gap-2 mt-4 text-[11px] text-zinc-500">
        <FileText className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>
          Invoice Now opens the job's unbilled labour, plant and prepared materials for review before creating an invoice. Supplier documents needing material details are shown for review. "Mark invoiced" records timesheet work invoiced outside Fieldlink and prevents that work from being billed again. Set your invoice cut-off day (e.g. the 20th) under Company Settings → Billing.
        </span>
      </div>
    </div>
  );
}
